import React, { useMemo, useState } from 'react';
import {
  AdminPageHeader,
  btnPrimary,
  btnSecondary,
  filtersRowClass,
  selectClass,
} from '../adminUi';
import { useGrading } from './GradingContext';
import { GradingScoreRange, GradingTypeBadge } from './GradingShared';
import { formatGradingScore, getCalibrationForFamily, runGradingDiagnostics } from './utils';

type PreviewMode = 'calibration' | 'scoring' | 'full';
type PreviewStep = 'family' | 'specialization' | 'role_cluster' | 'calibration' | 'scoring' | 'result';

export default function GradingPreviewTab() {
  const {
    families,
    roleClusters,
    specializations,
    roles,
    factors,
    questions,
    answers,
    calibrationQuestions,
    calibrationAnswers,
  } = useGrading();

  const [mode, setMode] = useState<PreviewMode>('full');
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState<PreviewStep>('family');
  const [selectedFamilyId, setSelectedFamilyId] = useState<number | null>(null);
  const [selectedSpecId, setSelectedSpecId] = useState<number | null>(null);
  const [selectedRoleClusterId, setSelectedRoleClusterId] = useState<number | null>(null);
  const [calibAnswerId, setCalibAnswerId] = useState<number | null>(null);
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<Record<number, number>>({});
  const [inferredRoleCluster, setInferredRoleCluster] = useState<number | null>(null);

  const diagnostics = useMemo(
    () => runGradingDiagnostics({
      families, roleClusters, roles, factors, questions, answers,
      calibrationQuestions, calibrationAnswers,
    }),
    [families, roleClusters, roles, factors, questions, answers, calibrationQuestions, calibrationAnswers],
  );

  const familyRoleClusters = useMemo(
    () => (selectedFamilyId ? roleClusters.filter((s) => s.family_id === selectedFamilyId) : []),
    [roleClusters, selectedFamilyId],
  );

  const familySpecs = useMemo(
    () => (selectedFamilyId ? specializations.filter((s) => s.family_id === selectedFamilyId) : []),
    [specializations, selectedFamilyId],
  );

  const hasSpecs = familySpecs.length > 0;

  const calib = useMemo(
    () => (selectedFamilyId ? getCalibrationForFamily(selectedFamilyId, calibrationQuestions, calibrationAnswers) : { question: null, answers: [] }),
    [selectedFamilyId, calibrationQuestions, calibrationAnswers],
  );

  const activeRoleClusterId = mode === 'scoring' ? selectedRoleClusterId : inferredRoleCluster;

  const scoringQuestions = useMemo(() => {
    if (!selectedFamilyId || !activeRoleClusterId) return [];
    const familyFactorIds = factors.filter((f) => f.family_id === selectedFamilyId).map((f) => f.id);
    return questions.filter((q) => q.role_cluster_id === activeRoleClusterId && familyFactorIds.includes(q.diff_factor_id));
  }, [selectedFamilyId, activeRoleClusterId, factors, questions]);

  const totalScore = useMemo(
    () => Object.values(selectedAnswerIds).reduce((sum, answerId) => {
      const answer = answers.find((a) => a.id === answerId);
      return sum + (answer?.answer_score ?? 0);
    }, 0),
    [selectedAnswerIds, answers],
  );

  const matchedRole = useMemo(() => {
    if (!activeRoleClusterId) return undefined;
    const candidates = roles.filter((r) => {
      if (r.role_cluster_id !== activeRoleClusterId) return false;
      if (selectedSpecId && r.specialization_id && r.specialization_id !== selectedSpecId) return false;
      if (r.min_score == null || r.max_score == null) return false;
      return totalScore >= r.min_score && totalScore <= r.max_score;
    });
    return candidates.sort((a, b) => b.grade_level - a.grade_level)[0];
  }, [roles, activeRoleClusterId, selectedSpecId, totalScore]);

  const resetPreviewState = () => {
    setSelectedFamilyId(null);
    setSelectedSpecId(null);
    setSelectedRoleClusterId(null);
    setCalibAnswerId(null);
    setSelectedAnswerIds({});
    setInferredRoleCluster(null);
  };

  const startPreview = () => {
    resetPreviewState();
    setStarted(true);
    setStep('family');
  };

  const goAfterFamily = () => {
    if (!selectedFamilyId) return;
    if (hasSpecs) {
      setStep('specialization');
      return;
    }
    if (mode === 'scoring') {
      setStep('role_cluster');
      return;
    }
    setStep('calibration');
  };

  const goAfterSpecialization = () => {
    if (mode === 'scoring') {
      setStep('role_cluster');
      return;
    }
    setStep('calibration');
  };

  const finishCalibration = () => {
    const answer = calib.answers.find((a) => a.id === calibAnswerId);
    setInferredRoleCluster(answer?.target_role_cluster_id ?? null);
    if (mode === 'calibration') setStep('result');
    else setStep('scoring');
  };

  const goAfterRoleCluster = () => {
    if (!selectedRoleClusterId) return;
    setStep('scoring');
  };

  const renderScoringQuestion = (q: (typeof questions)[0]) => {
    const qAnswers = answers.filter((a) => a.question_id === q.id);
    const factor = factors.find((f) => f.id === q.diff_factor_id);
    return (
      <div key={q.id} className="dc-grading-preview-question">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <GradingTypeBadge label="Scoring" variant="scoring" />
          {factor && <span className="text-xs text-slate-500">{factor.name}</span>}
        </div>
        <p className="font-medium text-slate-900">{q.question_text}</p>
        <div className="mt-3 space-y-2">
          {qAnswers.map((a) => (
            <label key={a.id} className={`dc-grading-preview-option ${selectedAnswerIds[q.id] === a.id ? 'dc-grading-preview-option-selected' : ''}`}>
              <input
                type="radio"
                name={`q-${q.id}`}
                checked={selectedAnswerIds[q.id] === a.id}
                onChange={() => setSelectedAnswerIds((p) => ({ ...p, [q.id]: a.id }))}
              />
              <span className="flex-1">{a.answer_text}</span>
              <span className="dc-grading-answer-score">{formatGradingScore(a.answer_score)} б.</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const stepItems = useMemo(() => {
    const items: { id: PreviewStep; label: string }[] = [{ id: 'family', label: 'Family' }];
    if (hasSpecs) items.push({ id: 'specialization', label: 'Specialization' });
    if (mode === 'scoring') items.push({ id: 'role_cluster', label: 'Role cluster' });
    if (mode !== 'scoring') items.push({ id: 'calibration', label: 'Калибровка' });
    if (mode !== 'calibration') items.push({ id: 'scoring', label: 'Scoring' });
    items.push({ id: 'result', label: 'Итог' });
    return items;
  }, [hasSpecs, mode]);

  return (
    <div>
      <AdminPageHeader
        title="Предпросмотр анкеты"
        subtitle="Family → optional Specialization → калибровка → scoring → Role"
      />

      {!started && (
        <div className={filtersRowClass}>
          <select className={selectClass} value={mode} onChange={(e) => setMode(e.target.value as PreviewMode)}>
            <option value="full">Полный сценарий</option>
            <option value="calibration">Только калибровка</option>
            <option value="scoring">Только scoring</option>
          </select>
          <button type="button" className={btnPrimary} onClick={startPreview} disabled={!families.length}>
            Запустить предпросмотр
          </button>
        </div>
      )}

      {diagnostics.length > 0 && (
        <div className="dc-grading-diagnostics mb-6">
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Диагностика дерева</h3>
          <ul>
            {diagnostics.map((d, i) => (
              <li key={i} className={d.level === 'error' ? 'dc-grading-diag-error' : 'dc-grading-diag-warn'}>
                <i className={`fas fa-${d.level === 'error' ? 'times-circle' : 'exclamation-triangle'} mr-2`} />
                {d.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {started && (
        <div className="dc-grading-preview-panel">
          <div className="dc-grading-preview-steps">
            {stepItems.map((item, index) => (
              <span key={item.id} className={step === item.id ? 'active' : ''}>
                {index + 1}. {item.label}
              </span>
            ))}
          </div>

          {step === 'family' && (
            <div>
              <h4 className="mb-3 font-medium">Шаг 1: Выбор Family</h4>
              {families.length === 0 ? (
                <p className="text-sm text-slate-500">Нет Family в дереве грейдинга</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {families.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className={`dc-grading-preview-family ${selectedFamilyId === f.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedFamilyId(f.id);
                        setSelectedSpecId(null);
                        setSelectedRoleClusterId(null);
                        setCalibAnswerId(null);
                        setSelectedAnswerIds({});
                        setInferredRoleCluster(null);
                      }}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className={`${btnPrimary} mt-4`}
                disabled={!selectedFamilyId}
                onClick={goAfterFamily}
              >
                Далее
              </button>
            </div>
          )}

          {step === 'specialization' && (
            <div>
              <h4 className="mb-3 font-medium">Specialization (опционально)</h4>
              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  className={`dc-grading-preview-family ${selectedSpecId === null ? 'selected' : ''}`}
                  onClick={() => setSelectedSpecId(null)}
                >
                  Без специализации
                </button>
                {familySpecs.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`dc-grading-preview-family ${selectedSpecId === s.id ? 'selected' : ''}`}
                    onClick={() => setSelectedSpecId(s.id)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <button type="button" className={btnSecondary} onClick={() => setStep('family')}>Назад</button>
                <button type="button" className={btnPrimary} onClick={goAfterSpecialization}>Далее</button>
              </div>
            </div>
          )}

          {step === 'role_cluster' && (
            <div>
              <h4 className="mb-3 font-medium">Выбор Role cluster</h4>
              {familyRoleClusters.length === 0 ? (
                <p className="text-sm text-slate-500">У выбранной Family нет Role cluster</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {familyRoleClusters.map((rc) => (
                    <button
                      key={rc.id}
                      type="button"
                      className={`dc-grading-preview-family ${selectedRoleClusterId === rc.id ? 'selected' : ''}`}
                      onClick={() => setSelectedRoleClusterId(rc.id)}
                    >
                      {rc.name}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <button type="button" className={btnSecondary} onClick={() => setStep(hasSpecs ? 'specialization' : 'family')}>Назад</button>
                <button type="button" className={btnPrimary} disabled={!selectedRoleClusterId} onClick={goAfterRoleCluster}>Далее</button>
              </div>
            </div>
          )}

          {step === 'calibration' && (
            <div>
              <h4 className="mb-3 font-medium">Калибровка: определение Role cluster</h4>
              {!calib.question ? (
                <p className="text-sm text-slate-500">Нет калибровочного вопроса для этой Family</p>
              ) : calib.answers.length === 0 ? (
                <p className="text-sm text-slate-500">У калибровочного вопроса нет ответов</p>
              ) : (
                <>
                  <p className="font-medium text-slate-900 mb-3">{calib.question.question_text}</p>
                  <div className="space-y-2">
                    {calib.answers.map((a) => {
                      const target = roleClusters.find((s) => s.id === a.target_role_cluster_id);
                      return (
                        <label key={a.id} className={`dc-grading-preview-option ${calibAnswerId === a.id ? 'dc-grading-preview-option-selected' : ''}`}>
                          <input
                            type="radio"
                            name="calib"
                            checked={calibAnswerId === a.id}
                            onChange={() => setCalibAnswerId(a.id)}
                          />
                          <span className="flex-1">{a.answer_text}</span>
                          <span className="text-xs text-indigo-600">→ {target?.name || `#${a.target_role_cluster_id}`}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
              <div className="mt-4 flex gap-2">
                <button type="button" className={btnSecondary} onClick={() => setStep(hasSpecs ? 'specialization' : 'family')}>Назад</button>
                <button
                  type="button"
                  className={btnPrimary}
                  disabled={!!calib.question && calib.answers.length > 0 && !calibAnswerId}
                  onClick={finishCalibration}
                >
                  Далее
                </button>
              </div>
            </div>
          )}

          {step === 'scoring' && (
            <div>
              <h4 className="mb-1 font-medium">Scoring-вопросы</h4>
              {activeRoleClusterId && (
                <p className="text-sm text-slate-600 mb-3">
                  Role cluster: <strong>{roleClusters.find((s) => s.id === activeRoleClusterId)?.name}</strong>
                </p>
              )}
              <p className="text-sm text-slate-500 mb-4">Текущая сумма: <strong>{formatGradingScore(totalScore)}</strong> баллов</p>
              {scoringQuestions.length === 0 ? (
                <p className="text-sm text-slate-500">Нет scoring-вопросов для этой Role cluster</p>
              ) : (
                scoringQuestions.map(renderScoringQuestion)
              )}
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => setStep(mode === 'scoring' ? 'role_cluster' : 'calibration')}
                >
                  Назад
                </button>
                <button type="button" className={btnPrimary} onClick={() => setStep('result')}>Завершить</button>
              </div>
            </div>
          )}

          {step === 'result' && (
            <div className="dc-grading-preview-result">
              <h4 className="mb-4 font-medium">Итоговый результат</h4>
              <dl className="dc-grading-result-grid">
                <div><dt>Family</dt><dd>{families.find((f) => f.id === selectedFamilyId)?.name || '—'}</dd></div>
                <div><dt>Specialization</dt><dd>{selectedSpecId ? specializations.find((s) => s.id === selectedSpecId)?.name : '—'}</dd></div>
                <div><dt>Role cluster</dt><dd>{activeRoleClusterId ? roleClusters.find((s) => s.id === activeRoleClusterId)?.name : '—'}</dd></div>
                <div><dt>Role</dt><dd>{matchedRole?.name || 'Не определена'}</dd></div>
                <div><dt>Grade</dt><dd>{matchedRole?.grade_level ?? '—'}</dd></div>
                <div><dt>Баллы</dt><dd>{formatGradingScore(totalScore)}</dd></div>
                <div><dt>Диапазон Role</dt><dd>{matchedRole ? <GradingScoreRange min={matchedRole.min_score} max={matchedRole.max_score} /> : '—'}</dd></div>
              </dl>
              <button
                type="button"
                className={`${btnSecondary} mt-4`}
                onClick={() => {
                  setStarted(false);
                  setStep('family');
                  resetPreviewState();
                }}
              >
                Начать заново
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
